import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { message } from 'antd';
import ErrorMessage from 'apis/ErrorMessage';
import {
  currencySetApi,
  otomeCurrencyApi,
  fetchLiveWallpaperDropApi,
  fetchCurrencyIconDropApi,
  fetchAbilityDropApi,
  fetchCurrencyDropApi,
} from 'apis/platform/currencyApis';
import { IDropListProps } from 'utils/types/common';

/// 재화 목록
export const useCurrencyList = (searchData) => {
  const { status, data, error, isFetching } = useQuery(
    ['currencyList', searchData],
    async () => await currencySetApi.read(searchData),
    {
      enabled: !!searchData,
    },
  );
  return { status, data, error, isFetching };
};

/// 디테일
export const useCurrencyDetail = (currencyId) => {
  const { status, data, error, isFetching } = useQuery(
    ['currencyDetail', currencyId],
    async () => await currencySetApi.detail(currencyId),
    {
      //   select: data => {
      //     console.log(data);
      //   },
    },
  );
  return { status, data, error, isFetching };
};

/// 등록 & 수정
export const useCurrencyInput = () => {
  const queryClient = useQueryClient();

  return useMutation(async (props: any) => await currencySetApi.update(props), {
    onMutate: (props) => {
      ('');
    },
    onSuccess: (props) => {
      queryClient.invalidateQueries(['currencyDetail']);
      message.success('재화가 입력되었습니다', 1);
    },
    onError: (props) => (err: any, variables, context) => {
      if (err?.response) {
        ErrorMessage(err.response.status, err.response.data);
      }
    },
  });
};

/// 오메 재화 조회(드레스)
export const useOtomeCurrencyList = (projectId) => {
  const { status, data, error, isFetching } = useQuery(
    ['otomeCurrencyList', projectId],
    async () => await otomeCurrencyApi.read(projectId),
    {
      enabled: !!projectId,
    },
  );
  return { status, data, error, isFetching };
};

/// 오토메 재화 상세 (오토메는 작품관리 안에 인앱 상품 메뉴로 귀속 되어 있음)
export const useOtomeCurrencyDetail = (currency?: string) => {
  const { status, data, error, isFetching } = useQuery(
    ['otomeCurrencyDetail', currency],
    async () => await otomeCurrencyApi.detail(currency),
  );
  return { status, data, error, isFetching };
};

/// 오토메 재화 업데이트 (오토메는 작품관리 안에 인앱 상품 메뉴로 귀속 되어 있음)
export const useOtomeDressUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (props: any) => await otomeCurrencyApi.update(props),
    {
      onMutate: (props) => {
        ('');
      },
      onSuccess: (props) => {
        queryClient.invalidateQueries(['otomeCurrencyDetail']);
        message.success('재화가 입력되었습니다', 1);
      },
      onError: (props) => (err: any, variables, context) => {
        if (err?.response) {
          ErrorMessage(err.response.status, err.response.data);
        }
      },
    },
  );
};

/// 캐릭터 모델 드롭다운 (standing)
export const useCharacterStandingDropList = (projectId) => {
  const { status, data, error, isFetching } = useQuery(
    ['standingDropList', projectId],
    async () => await fetchCurrencyDropApi.standing(projectId),
    {
      select: (data) => {
        const newArr: IDropListProps[] = [];
        data.map((v, i) =>
          newArr.push({ key: i, value: v.model_id, text: v.model_name }),
        );
        return newArr;
      },
    },
  );
  return { status, data, error, isFetching };
};

/// 연결 배경 드롭다운
export const useBackgroundDropList = (projectId) => {
  const { status, data, error, isFetching } = useQuery(
    ['backgroundDropList', projectId],
    async () => await fetchCurrencyDropApi.background(projectId),
    {
      select: (data) => {
        return data.map((v, i) => ({ ...v, key: i, design_id: v.bg_id }));
      },
    },
  );
  return { status, data, error, isFetching };
};

/// Live 배경 드롭다운 (wallpaper)
export const useLiveWallpaperDropList = (projectId) => {
  const { status, data, error, isFetching } = useQuery(
    ['liveWallpaperDropList', projectId],
    async () => await fetchLiveWallpaperDropApi(projectId),
    {
      select: (data) => {
        const newArr: IDropListProps[] = [];
        data.map((v, i) =>
          newArr.push({ key: i, value: v.model_id, text: v.model_name }),
        );
        return newArr;
      },
    },
  );
  return { status, data, error, isFetching };
};

/// 리소스 드롭다운
export const useCurrencyResourceDropList = (projectId, currencyType) => {
  const { status, data, error, isFetching } = useQuery(
    ['currencyResourceDropList', projectId, currencyType],
    async () => await fetchCurrencyDropApi.resource(projectId, currencyType),
    {
      enabled: !!projectId,
      onSuccess: (data) => {
        return data.map((v, i) => ({ ...v, key: i }));
      },
    },
  );
  return { status, data, error, isFetching };
};

/// 아이콘 드롭다운
export const useCurrencyIconDropList = (projectId) => {
  const { status, data, error, isFetching } = useQuery(
    ['currencyIconDropList', projectId],
    async () => await fetchCurrencyIconDropApi(projectId),
    {
      enabled: !!projectId,
      onSuccess: (data) => {
        return data.map((v, i) => ({ ...v, key: i }));
      },
    },
  );
  return { status, data, error, isFetching };
};

/// 능력치 - 적용 캐릭터
export const useAbilityCharacterDropList = (projectId) => {
  const { status, data, error, isFetching } = useQuery(
    ['abilityCharacterDropList', projectId],
    async () => await fetchAbilityDropApi.speaker(projectId),
    {
      enabled: !!projectId,
      select: (data) => {
        // console.log(data);
        const newArr: IDropListProps[] = [];
        data.map((v, i) =>
          newArr.push({ key: i, value: v.speaker, text: v.speaker }),
        );
        return newArr;
      },
      onSuccess: (data) => {
        // return data.map((v, i) => ({ ...v, key: i }));
      },
    },
  );
  return { status, data, error, isFetching };
};

/// 능력치 - 적용 능력
export const useAbilityDropList = (projectId, speaker) => {
  const result = useQueries({
    queries: [
      {
        queryKey: ['abilityDropList', projectId, speaker[0]],
        queryFn: async () =>
          await fetchAbilityDropApi.ability(projectId, speaker[0]),
        enabled: !!projectId,
        select: (data) => {
          const newArr: IDropListProps[] = [];
          data.map((v, i) =>
            newArr.push({ key: i, value: v.ability_id, text: v.ability_name }),
          );
          newArr.push({ key: newArr.length, value: -1, text: '능력치 없음' });
          return newArr;
        },
      },
      {
        queryKey: ['abilityDropList', projectId, speaker[1]],
        queryFn: async () =>
          await fetchAbilityDropApi.ability(projectId, speaker[1]),
        enabled: !!projectId,
        select: (data) => {
          const newArr: IDropListProps[] = [];
          data.map((v, i) =>
            newArr.push({ key: i, value: v.ability_id, text: v.ability_name }),
          );
          newArr.push({ key: newArr.length, value: -1, text: '능력치 없음' });
          return newArr;
        },
      },
    ],
  });
  return { result };
};

/// 오토메 - 획득 경로
export const useOtomeProductTypeDropList = () => {
  const { status, data, error, isFetching } = useQuery(
    ['otomeProductTypeDropList'],
    async () => await fetchCurrencyDropApi.otomeProductType(),
    {
      select: (data) => {
        const newArr: IDropListProps[] = [];
        data.map((v, i) =>
          newArr.push({ key: i, value: v.code, text: v.code_name }),
        );
        return newArr;
      },
    },
  );
  return { status, data, error, isFetching };
};
